import { useCallback, useEffect, useRef, useState } from 'react';
import { getMfaStatusRequest, resetMfaEnrollmentRequest } from 'api/requests/user-mfa-requests';
import IMfaStatus from 'types/auth/IMfaStatus';
import logError from 'utils/log-error';

export interface UseMfaReset {
  status: IMfaStatus | null;
  isLoading: boolean;
  isResetting: boolean;
  isUnavailable: boolean;
  hasFailed: boolean;
  hasSucceeded: boolean;
  reset: () => Promise<void>;
}

const useMfaReset = (userId: number): UseMfaReset => {
  const [status, setStatus] = useState<IMfaStatus | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [isUnavailable, setIsUnavailable] = useState<boolean>(false);
  const [hasFailed, setHasFailed] = useState<boolean>(false);
  const [hasSucceeded, setHasSucceeded] = useState<boolean>(false);

  const inFlight = useRef<boolean>(false);
  const isMounted = useRef<boolean>(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadStatus = useCallback(async (): Promise<void> => {
    try {
      const result = await getMfaStatusRequest(userId);
      if (!isMounted.current) return;
      setStatus(result);
      setIsUnavailable(false);
    } catch (error) {
      logError(error);
      if (!isMounted.current) return;
      setStatus(null);
      setIsUnavailable(true);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setIsLoading(true);
    setHasFailed(false);
    setHasSucceeded(false);
    loadStatus();
  }, [loadStatus]);

  const reset = useCallback(async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsResetting(true);
    setHasFailed(false);
    setHasSucceeded(false);

    try {
      await resetMfaEnrollmentRequest(userId);
      if (isMounted.current) setHasSucceeded(true);
      await loadStatus();
    } catch (error) {
      logError(error);
      if (isMounted.current) setHasFailed(true);
    } finally {
      inFlight.current = false;
      if (isMounted.current) setIsResetting(false);
    }
  }, [loadStatus, userId]);

  return { status, isLoading, isResetting, isUnavailable, hasFailed, hasSucceeded, reset };
};

export default useMfaReset;
